import { useEffect, useState } from "react";
import { CornerDownLeft, Loader2, X } from "lucide-react";
import api, { formatApiError } from "../../../lib/api";
import SignaturePad from "../../SignaturePad";
import DamageInspector from "./DamageInspector";
import { isoToEU } from "../../../lib/format";

const FUEL_LEVELS = ["8/8", "7/8", "6/8", "5/8", "4/8", "3/8", "2/8", "1/8"];

/** v4.7.1 — Vehicle Return Inspection (desk drop-off / check-out). */
export default function ReturnModal({ booking, onClose, onDone }) {
  const startKm = Number(booking?.handover_odometer || 0);
  const [odometer, setOdometer] = useState(startKm ? String(startKm) : "");
  const [fuel, setFuel] = useState(booking?.handover_fuel_level || "8/8");
  const [notes, setNotes] = useState("");
  const [damages, setDamages] = useState([]);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const v = booking?.vehicle_snapshot || {};
  const driven = Number(odometer || 0) - startKm;

  // v4.7.3 — Escape closes the modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/return`, {
        return_odometer: odometer === "" ? null : Number(odometer),
        return_fuel_level: fuel,
        return_notes: notes,
        damages,
        signature,
      });
      onDone?.(data.booking);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="return-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] w-full max-w-lg rounded border border-black/10 dark:border-white/10 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400">
              Desk drop-off
            </p>
            <h2 className="font-display text-2xl mt-1">Vehicle return inspection</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1.5 -mr-1 -mt-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors"
            data-testid="return-close"
          >
            <X size={18} />
          </button>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3 text-sm" data-testid="return-summary">
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Driver</dt>
            <dd className="font-semibold">{booking.full_name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Assigned plate</dt>
            <dd className="font-mono uppercase">{booking.assigned_plate || v.license_plate || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Vehicle</dt>
            <dd className="font-semibold">{[v.brand, v.name].filter(Boolean).join(" ") || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Due back</dt>
            <dd>{isoToEU(booking.return_date)} · {booking.return_time || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Start odometer</dt>
            <dd className="tabular-nums">{startKm ? `${startKm.toLocaleString()} km` : "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Distance driven</dt>
            <dd className="tabular-nums" data-testid="return-driven">
              {startKm && Number(odometer) >= startKm ? `${driven.toLocaleString()} km` : "—"}
            </dd>
          </div>
        </dl>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">End odometer (km)</span>
            <input
              type="number"
              min={startKm || 0}
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-black dark:focus:border-white"
              data-testid="return-odometer"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Fuel level</span>
            <select
              value={fuel}
              onChange={(e) => setFuel(e.target.value)}
              className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none"
              data-testid="return-fuel"
            >
              {FUEL_LEVELS.map((f) => (
                <option key={f} value={f} className="text-black">{f}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">
            Return condition / damage notes
          </span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="New dent on driver door, fuel short by 1/8…"
            className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-black dark:focus:border-white"
            data-testid="return-notes"
          />
        </label>

        <div className="mt-5">
          <DamageInspector
            damages={damages}
            onChange={setDamages}
            existing={booking.handover_damages || []}
            testId="return-damage"
          />
        </div>

        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            Return acknowledgment signature (optional)
          </p>
          <SignaturePad onChange={setSignature} height={140} />
        </div>

        {error && (
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-4" data-testid="return-error">{error}</p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="border border-black/15 dark:border-white/15 px-4 py-2 text-sm rounded hover:border-black dark:hover:border-white"
            data-testid="return-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="bg-emerald-600 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            data-testid="return-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CornerDownLeft size={14} />}
            Confirm return &amp; complete rental
          </button>
        </div>
      </div>
    </div>
  );
}
